import type { WorkplaceRequestTypeFieldType } from "@gettactic/api";

/**
 * An info block is admin prose placed in the field list, not a question. It has
 * no label to show, nothing to require, and no answer to collect.
 */
export const isInfoBlockFieldType = (
  fieldType: WorkplaceRequestTypeFieldType
): boolean => fieldType === "info_block";

/**
 * A reservation field asks for a booking: one or more days, a start and end
 * time, and — where its own config allows it — a recurrence and attendees.
 *
 * It is the only field type that carries a `config`, and the only one whose
 * answer is an object rather than a scalar, so several call sites branch on it.
 */
export const isReservationFieldType = (
  fieldType: WorkplaceRequestTypeFieldType
): boolean => fieldType === "reservation";

/**
 * Whether a field carries an answer. Submitting a value for one that doesn't is
 * a 400 (`ErrFieldNotSubmittable`), so every path that builds `field_values` —
 * the new-request wizard, the edit modal — has to filter on this.
 */
export const isSubmittableFieldType = (
  fieldType: WorkplaceRequestTypeFieldType
): boolean => !isInfoBlockFieldType(fieldType);

/**
 * Whether a field's answers reach an anonymous viewer of a public dashboard.
 *
 * Mirrors the allow-list the backend applies before publishing a field value:
 * an info block carries no answer, and a file upload would hand out a download
 * URL, so neither is ever published. A field type outside this list has no
 * value in the payload at all — so a column for it would always read empty, and
 * offering one is a dead end.
 *
 * Kept as an explicit list, not a negation, so a field type added later is
 * excluded until it is reviewed here — the same way the backend does it.
 */
export const isPubliclyPublishedFieldType = (
  fieldType: WorkplaceRequestTypeFieldType
): boolean =>
  fieldType === "short_text" ||
  fieldType === "long_text" ||
  fieldType === "single_select" ||
  fieldType === "multi_select" ||
  fieldType === "toggle" ||
  fieldType === "number" ||
  fieldType === "date" ||
  fieldType === "time" ||
  fieldType === "person" ||
  fieldType === "seat";
